import { Box, Tooltip, Typography } from '@mui/material';
import { LaneSegment, Marker, SegmentKind, Snap, hhmm } from './reportData';

const HOUR = 60;

export const SEGMENT_COLORS: Record<SegmentKind, string> = {
  work: '#4f8df7',
  break: '#5cc46a',
  additional: '#9b6bf2',
  absence: '#e25c7a',
  worked: '#4f8df7',
  'actual-break': '#5cc46a',
  'open-break': '#d9a800'
};

/** Fits the scale to the shift's own extremes, padded one hour each side and aligned to whole hours. Empty data falls back to 0–24h. */
export const timelineScale = (segments: LaneSegment[], markers: Marker[]): { min: number; max: number; ticks: number[] } => {
  const values = [...segments.flatMap((s) => [s.start, s.end]), ...markers.map((m) => m.minutes)];
  const min = values.length > 0 ? Math.floor(Math.min(...values) / HOUR) * HOUR - HOUR : 0;
  const max = values.length > 0 ? Math.ceil(Math.max(...values) / HOUR) * HOUR + HOUR : 1440;
  const span = max - min;
  const step = span > 12 * HOUR ? 3 * HOUR : span > 6 * HOUR ? 2 * HOUR : HOUR;
  const ticks: number[] = [];
  for (let t = min; t < max; t += step) ticks.push(t);
  ticks.push(max);
  return { min, max, ticks };
};

const Lane = ({ name, segments, min, max }: { name: string; segments: LaneSegment[]; min: number; max: number }) => {
  const pct = (m: number) => `${((m - min) / (max - min)) * 100}%`;
  return (
    <>
      <Typography variant="caption" color="text.secondary" noWrap title={name} sx={{ fontFamily: 'monospace' }}>
        {name}
      </Typography>
      <Box sx={{ position: 'relative', height: 20, bgcolor: 'action.hover', borderRadius: 1 }}>
        {segments.map((s, i) => (
          <Tooltip key={`${s.label}-${i}`} title={s.label}>
            <Box
              data-testid={s.kind === 'open-break' ? 'open-break-segment' : 'lane-segment'}
              sx={{
                position: 'absolute',
                left: pct(s.start),
                width: `calc(${pct(s.end)} - ${pct(s.start)})`,
                top: s.kind === 'break' || s.kind === 'actual-break' || s.kind === 'open-break' || s.thin ? 5 : 2,
                bottom: s.kind === 'break' || s.kind === 'actual-break' || s.kind === 'open-break' || s.thin ? 5 : 2,
                borderRadius: 0.5,
                // Hairline in the page color so adjacent blocks (work | break | work in a ranges lane) stay separable.
                boxSizing: 'border-box',
                border: '1px solid',
                borderColor: 'background.paper',
                bgcolor: s.kind === 'open-break' ? 'transparent' : SEGMENT_COLORS[s.kind],
                backgroundImage: s.kind === 'open-break' ? `repeating-linear-gradient(45deg, ${SEGMENT_COLORS[s.kind]}, ${SEGMENT_COLORS[s.kind]} 4px, transparent 4px, transparent 8px)` : undefined
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </>
  );
};

export const ShiftTimeline = ({
  expected,
  actual,
  markers,
  tolerance = [],
  ranges = [],
  snaps = [],
  actualWithAbsences = [],
  toleranceWithAbsences = [],
  actualRanges = [],
  actualRangesWithTolerance = [],
  absenceRanges = [],
  nightRanges = [],
  nightRangesWithTolerance = [],
  earlyLateRanges = [],
  restDayRanges = []
}: {
  expected: LaneSegment[];
  actual: LaneSegment[];
  markers: Marker[];
  tolerance?: LaneSegment[];
  ranges?: LaneSegment[];
  snaps?: Snap[];
  actualWithAbsences?: LaneSegment[];
  toleranceWithAbsences?: LaneSegment[];
  actualRanges?: LaneSegment[];
  actualRangesWithTolerance?: LaneSegment[];
  absenceRanges?: LaneSegment[];
  nightRanges?: LaneSegment[];
  nightRangesWithTolerance?: LaneSegment[];
  earlyLateRanges?: LaneSegment[];
  restDayRanges?: LaneSegment[];
}) => {
  const { min, max, ticks } = timelineScale(
    [...expected, ...actual, ...tolerance, ...ranges, ...actualWithAbsences, ...toleranceWithAbsences, ...actualRanges, ...actualRangesWithTolerance, ...absenceRanges, ...nightRanges, ...nightRangesWithTolerance, ...earlyLateRanges, ...restDayRanges],
    markers
  );
  const pct = (m: number) => `${((m - min) / (max - min)) * 100}%`;
  return (
    <Box data-testid="shift-timeline" sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', rowGap: 0.5, columnGap: 1, alignItems: 'center', my: 1 }}>
      {/* Lanes are named by the plugin they come from — the same ids as the pipeline view and "other outputs" — so nothing needs mapping as more punch/range plugins land. */}
      <Lane name="expected_punches" segments={expected} min={min} max={max} />
      {ranges.length > 0 && <Lane name="expected_ranges" segments={ranges} min={min} max={max} />}
      {absenceRanges.length > 0 && <Lane name="absence_ranges" segments={absenceRanges} min={min} max={max} />}
      <Lane name="actual_punches" segments={actual} min={min} max={max} />
      {tolerance.length > 0 && <Lane name="actual_punches_with_tolerance" segments={tolerance} min={min} max={max} />}
      {actualWithAbsences.length > 0 && <Lane name="actual_punches_with_absences" segments={actualWithAbsences} min={min} max={max} />}
      {toleranceWithAbsences.length > 0 && <Lane name="actual_punches_with_tolerance_and_absences" segments={toleranceWithAbsences} min={min} max={max} />}
      {actualRanges.length > 0 && <Lane name="actual_ranges" segments={actualRanges} min={min} max={max} />}
      {actualRangesWithTolerance.length > 0 && <Lane name="actual_ranges_with_tolerance" segments={actualRangesWithTolerance} min={min} max={max} />}
      {earlyLateRanges.length > 0 && <Lane name="early_late_ranges" segments={earlyLateRanges} min={min} max={max} />}
      {nightRanges.length > 0 && <Lane name="night_ranges" segments={nightRanges} min={min} max={max} />}
      {nightRangesWithTolerance.length > 0 && <Lane name="night_ranges_with_tolerance" segments={nightRangesWithTolerance} min={min} max={max} />}
      {restDayRanges.length > 0 && <Lane name="it_rest_day_ranges" segments={restDayRanges} min={min} max={max} />}
      <Box />
      <Box sx={{ position: 'relative', height: 16 }}>
        {ticks.map((t) => (
          <Typography key={t} variant="caption" sx={{ position: 'absolute', left: pct(t), transform: 'translateX(-50%)', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {hhmm(t)}
          </Typography>
        ))}
        {markers.map((m, i) => (
          <Tooltip key={`${m.label}-${i}`} title={m.label}>
            <Box data-testid="punch-marker" sx={{ position: 'absolute', left: pct(m.minutes), top: -46, width: 2, height: 44, bgcolor: 'text.primary', opacity: 0.55 }} />
          </Tooltip>
        ))}
        {snaps.map((snap, i) => (
          <Tooltip key={`${snap.key}-${i}`} title={`${snap.key} ${hhmm(snap.from)} → ${hhmm(snap.to)} (snapped)`}>
            <Box data-testid="snap-marker" sx={{ position: 'absolute', left: pct(snap.to), top: -12, width: 8, height: 8, borderRadius: '50%', bgcolor: SEGMENT_COLORS['open-break'], border: '1px solid', borderColor: 'background.paper' }} />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};
